import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BrainCircuit, Search, Sparkles, Loader2, Network, X, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

export default function Encyclopedia() {
  const [state, setState] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [ask, setAsk] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/brain/state").then(({ data }) => setState(data)).catch(() => {});
    api.get("/brain/nodes?limit=24").then(({ data }) => setNodes(data.nodes)).catch(() => {});
  }, []);

  const runSearch = async (e) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.post("/brain/search", { query: query.trim(), limit: 18 });
      setResults(data.results);
    } catch { setResults([]); } finally { setSearching(false); }
  };

  const runAsk = async () => {
    if (ask.trim().length < 4) return;
    setAsking(true); setAnswer(null);
    try {
      const { data } = await api.post("/brain/rag", { query: ask.trim() });
      setAnswer(data);
    } catch { setAnswer({ answer: "The brain couldn't answer that right now.", sources: [] }); }
    finally { setAsking(false); }
  };

  const openNode = async (id) => {
    setActive({ loading: true });
    try { const { data } = await api.get(`/brain/nodes/${id}`); setActive(data); }
    catch { setActive(null); }
  };

  const shown = results !== null ? results : nodes;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="encyclopedia-page">
      <Seo title="Gaming Encyclopedia — A Living Knowledge Brain" description="Explore an AI knowledge graph built from an academic history of video games. Ask questions, search by meaning, and traverse connected concepts — a living document, not a blog." />

      <div className="mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-lime/15 border border-neon-lime/50 text-neon-lime text-xs font-bold uppercase tracking-widest mb-4">
          <BrainCircuit className="w-3.5 h-3.5" /> Living Knowledge Graph
        </span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">Gaming <span className="text-neon-lime">Brain</span></h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">An evolving knowledge graph that digests and connects gaming history. It grows as new material is fed in — ask it anything, search by meaning, or follow the connections between ideas.</p>
        {state && (
          <div className="flex flex-wrap gap-4 mt-5 text-sm font-mono text-zinc-500" data-testid="brain-state">
            <span><span className="text-neon-cyan font-bold">{state.nodes}</span> nodes</span>
            <span><span className="text-neon-pink font-bold">{state.edges}</span> connections</span>
            <span><span className="text-neon-yellow font-bold">{state.avg_degree}</span> avg links/node</span>
          </div>
        )}
      </div>

      {/* Ask the Brain (GraphRAG) */}
      <div className="rounded-2xl border-2 border-neon-lime/40 bg-neon-lime/5 p-6 mb-10" data-testid="ask-brain">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-neon-lime" />
          <h2 className="font-display font-black text-xl uppercase tracking-tight">Ask the Brain</h2>
        </div>
        <Textarea value={ask} onChange={(e) => setAsk(e.target.value)} data-testid="ask-input" rows={2}
          placeholder="e.g. What was the ludology vs narratology debate? How did the indie renaissance happen?" />
        <Button onClick={runAsk} disabled={asking || ask.trim().length < 4} data-testid="ask-submit"
          className="mt-3 bg-neon-lime text-black rounded-none font-bold uppercase tracking-widest h-11 px-6 hover:bg-white">
          {asking ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ask"}
        </Button>
        {answer && (
          <div className="mt-5 border-t border-zinc-800 pt-4" data-testid="ask-answer">
            <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{answer.answer}</p>
            {answer.sources?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {answer.sources.map((s) => (
                  <button key={s.node_id} onClick={() => openNode(s.node_id)}
                    className="text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-zinc-700 text-zinc-300 hover:border-neon-lime hover:text-neon-lime transition-colors">
                    {s.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search */}
      <form onSubmit={runSearch} className="relative max-w-xl mb-6" data-testid="brain-search-form">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="brain-search-input"
          placeholder="Search concepts by meaning…"
          className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-neon-cyan outline-none rounded-full pl-11 pr-36 py-3 text-white" />
        {results !== null && (
          <button type="button" onClick={() => { setQuery(""); setResults(null); }} data-testid="brain-search-clear"
            className="absolute right-[6.75rem] top-1/2 -translate-y-1/2 z-10 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        )}
        <button type="submit" disabled={searching || query.trim().length < 2} data-testid="brain-search-submit"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-neon-cyan text-black font-bold uppercase text-xs tracking-widest rounded-full px-4 py-2 disabled:opacity-50">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </button>
      </form>

      <div className="flex items-center gap-2 mb-5">
        <BookOpen className="w-4 h-4 text-zinc-400" />
        <h2 className="font-display font-bold uppercase tracking-widest text-sm text-zinc-400">
          {results !== null ? `${shown.length} matches` : "Browse the graph"}
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((n, i) => (
          <motion.button key={n.node_id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 9) * 0.03 }}
            onClick={() => openNode(n.node_id)} data-testid={`kb-node-${i}`}
            className="text-left bg-zinc-950 border-2 border-zinc-800 hover:border-neon-lime rounded-xl p-4 transition-colors group">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-neon-lime">{n.node_type}</span>
              {typeof n.degree === "number" && <span className="text-[10px] font-mono text-zinc-600 flex items-center gap-1"><Network className="w-3 h-3" />{n.degree}</span>}
            </div>
            <h3 className="font-display font-bold uppercase tracking-tight leading-tight group-hover:text-neon-lime transition-colors line-clamp-2">{n.title}</h3>
            <p className="text-sm text-zinc-500 mt-2 line-clamp-3">{n.content}</p>
          </motion.button>
        ))}
      </div>

      {/* Node detail */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-zinc-950 border-2 border-neon-lime max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="node-detail">
          {active?.loading ? (
            <>
              <DialogTitle className="sr-only">Loading concept</DialogTitle>
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neon-lime" /></div>
            </>
          ) : active?.node ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-black uppercase tracking-tight">{active.node.title}</DialogTitle>
              </DialogHeader>
              <p className="text-[10px] font-bold uppercase tracking-widest text-neon-lime">{active.node.source} · {active.node.node_type}</p>
              <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap mt-2">{active.node.content}</p>
              {active.neighbors?.length > 0 && (
                <div className="mt-5 border-t border-zinc-800 pt-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1"><Network className="w-3.5 h-3.5" /> Connected concepts</p>
                  <div className="flex flex-wrap gap-2">
                    {active.neighbors.map((nb) => (
                      <button key={nb.node_id} onClick={() => openNode(nb.node_id)} data-testid="neighbor-chip"
                        className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:border-neon-cyan hover:text-neon-cyan transition-colors">
                        {nb.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
